<template>
  <DialogHeader>
    <template #right>
      <div
        class="font-medium text-green-900 cursor-pointer"
        @click="handleSave"
      >
        Save
      </div>
    </template>
    Add new copyright
  </DialogHeader>
  <DialogContent>
    <Float.Base class="w-full mb-[26px]">
      <Float.Trigger>
        <div class="relative">
          <UiInput
            :modelValue="' '"
            label="Copyright owner"
            class="pointer-events-none!"
            :tabindex="-1"
          />
          <div class="absolute flex items-center top-[22px] left-[17px]">
            <Avatar
              :src="selectedCopyrightOwner?.avatarUrl"
              :size="20"
              class="mr-2"
            />
            <div>{{ selectedCopyrightOwner?.name }}</div>
          </div>
        </div>
      </Float.Trigger>

      <Float.Component class="w-[calc(100%-24px)]">
        <CopyrightOwnersList class="w-full" />
      </Float.Component>
    </Float.Base>

    <div class="text-gray-800/75 text-[13px] leading-[16px] mb-3">
      <span class="font-medium">IMPORTANT</span>: Filename is permanent and
      cannot be changed once saved
    </div>

    <div class="relative mb-6">
      <UiInput v-model="filename" label="Filename" />
      <div class="absolute bottom-2 right-[17px] font-primary">
        {{ extension }}
      </div>
    </div>

    <div class="text-[12px] opacity-75 mb-[11px]">Upload to:</div>
    <div
      class="w-full h-[64px] rounded-sm p-[14px] flex items-center bg-gray-800/5 mb-[26px] cursor-pointer"
      @click="handleFolderClick"
    >
      <ThumbnailIcon :icon-size="36" :type="ItemType.Folder" class="mr-2.5" />
      <div>{{ selectedFolder.name }}</div>
      <div class="text-green-900 ml-auto">Change</div>
    </div>

    <div class="text-[12px] opacity-75 mb-[14px]">
      Title and description can be added at any time
    </div>

    <UiInput v-model="title" label="Tile (optional)" class="mb-[14px]" />

    <UiTextarea v-model="description" label="Description (optional)" />
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton class="w-1/2 tablet-h:w-auto" primary @click="handleSave">
      Save
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { computed, ref } from 'vue'
import {
  UiInput,
  Avatar,
  ThumbnailIcon,
  Float,
  UiTextarea,
  UiButton,
} from '@dacr/ui'
import {
  type Folder,
  ItemType,
  useCopyrightOwnersQuery,
} from '@/shared/graphql/legacy/generated'
import CopyrightOwnersList from '@/features/copyright/components/CopyrightOwnersList.vue'

const { close, open } = useDialog()

const { result } = useCopyrightOwnersQuery()

const props = defineProps<{
  file: File
}>()

const filename = ref('')
const selectedFolder = ref<Folder>({
  name: 'Dacr',
  id: 'root',
} as Folder)
const title = ref('')
const description = ref('')

const extension = computed(() => props.file.name.match(/.[0-9a-z]+$/i)?.[0])

const selectedCopyrightOwner = computed(() =>
  result.value?.copyrightOwners.items.find((item) => item.isSelected)
)

const handleFolderClick = () => {
  open(Dialogs.SelectFolder, {
    stack: true,
    events: {
      close(folder: Folder) {
        if (!folder) return
        selectedFolder.value = folder
      },
    },
  })
}

const handleSave = () => {
  close({
    filename: `${filename.value}${extension.value}` || props.file.name,
    title: title.value,
    description: description.value,
    selectedFolderId: selectedFolder.value.id,
  })
}
</script>
